import React, { useState, useEffect } from 'react';
import { gamificationApi } from '../api';

interface Achievement {
  id: number;
  code: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  points: number;
  condition_type: string;
  condition_value: number;
  rarity: string;
}

interface UserAchievement {
  id: number;
  achievement_id: number;
  achievement: Achievement;
  progress: number;
  is_unlocked: boolean;
  unlocked_at: string | null;
}

const AchievementList: React.FC = () => {
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [userAchievements, setUserAchievements] = useState<UserAchievement[]>([]);
  const [filter, setFilter] = useState<'all' | 'unlocked' | 'locked'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAchievements();
  }, []);

  const loadAchievements = async () => {
    try {
      setLoading(true);
      const [allRes, userRes] = await Promise.all([
        gamificationApi.getAllAchievements(),
        gamificationApi.getUserAchievements(),
      ]);
      setAchievements(Array.isArray(allRes.data) ? allRes.data : []);
      setUserAchievements(Array.isArray(userRes.data) ? userRes.data : []);
    } catch (error) {
      console.error('加载成就失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return { bg: '#e0e0e0', text: '#666', border: '#bdbdbd' };
      case 'rare': return { bg: '#e3f2fd', text: '#1976d2', border: '#2196f3' };
      case 'epic': return { bg: '#f3e5f5', text: '#7b1fa2', border: '#9c27b0' };
      case 'legendary': return { bg: '#fff3e0', text: '#e65100', border: '#ff9800' };
      default: return { bg: '#e0e0e0', text: '#666', border: '#bdbdbd' };
    }
  };

  const getRarityLabel = (rarity: string) => {
    switch (rarity) {
      case 'common': return '普通';
      case 'rare': return '稀有';
      case 'epic': return '史诗';
      case 'legendary': return '传说';
      default: return rarity;
    }
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      'social': '社交',
      'diary': '日记',
      'analysis': '分析',
      'relationship': '关系',
      'task': '任务',
      'level': '等级',
    };
    return labels[category] || category;
  };

  const getUserAchievementStatus = (achievementId: number) => {
    return userAchievements.find(ua => ua.achievement_id === achievementId);
  };

  const categories = ['all', ...Array.from(new Set(achievements.map(a => a.category)))];

  const filteredAchievements = achievements.filter(achievement => {
    const userAch = getUserAchievementStatus(achievement.id);
    const isUnlocked = userAch?.is_unlocked || false;

    if (filter === 'unlocked' && !isUnlocked) return false;
    if (filter === 'locked' && isUnlocked) return false;
    if (categoryFilter !== 'all' && achievement.category !== categoryFilter) return false;

    return true;
  });

  const unlockedCount = achievements.filter(a => getUserAchievementStatus(a.id)?.is_unlocked).length;
  const totalPoints = achievements
    .filter(a => getUserAchievementStatus(a.id)?.is_unlocked)
    .reduce((sum, a) => sum + a.points, 0);

  if (loading) {
    return <div className="loading">加载成就中...</div>;
  }

  return (
    <div className="achievement-list">
      {/* 统计信息 */}
      <div className="achievement-stats">
        <div className="stat">
          <span className="stat-value">{unlockedCount}/{achievements.length}</span>
          <span className="stat-label">已解锁</span>
        </div>
        <div className="stat">
          <span className="stat-value">{totalPoints}</span>
          <span className="stat-label">成就点数</span>
        </div>
      </div>

      {/* 筛选器 */}
      <div className="filters">
        <div className="filter-group">
          <label>状态:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value as any)}>
            <option value="all">全部</option>
            <option value="unlocked">已解锁</option>
            <option value="locked">未解锁</option>
          </select>
        </div>
        <div className="filter-group">
          <label>类别:</label>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="all">全部</option>
            {categories.filter(c => c !== 'all').map(cat => (
              <option key={cat} value={cat}>{getCategoryLabel(cat)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* 成就列表 */}
      <div className="achievements-grid">
        {filteredAchievements.map(achievement => {
          const userAch = getUserAchievementStatus(achievement.id);
          const isUnlocked = userAch?.is_unlocked || false;
          const progress = userAch?.progress || 0;
          const rarityColors = getRarityColor(achievement.rarity);

          return (
            <div
              key={achievement.id}
              className={`achievement-card ${isUnlocked ? 'unlocked' : 'locked'}`}
              style={{
                borderColor: isUnlocked ? rarityColors.border : '#ddd',
                backgroundColor: isUnlocked ? rarityColors.bg : '#f5f5f5',
              }}
            >
              <div className="achievement-icon" style={{ opacity: isUnlocked ? 1 : 0.4 }}>
                {achievement.icon}
              </div>
              <div className="achievement-info">
                <div className="achievement-header">
                  <span className="achievement-name" style={{ color: isUnlocked ? rarityColors.text : '#999' }}>
                    {achievement.name}
                  </span>
                  <span 
                    className="achievement-rarity"
                    style={{ 
                      backgroundColor: rarityColors.border,
                      color: 'white',
                    }}
                  >
                    {getRarityLabel(achievement.rarity)}
                  </span>
                </div>
                <p className="achievement-desc">{achievement.description}</p>
                <div className="achievement-footer">
                  <span className="achievement-points">🏅 {achievement.points}点</span>
                  {!isUnlocked && achievement.condition_value && (
                    <span className="achievement-progress">
                      进度: {progress}/{achievement.condition_value}
                    </span>
                  )}
                  {isUnlocked && userAch?.unlocked_at && (
                    <span className="achievement-date">
                      {new Date(userAch.unlocked_at).toLocaleDateString('zh-CN')}
                    </span>
                  )}
                </div>
                {!isUnlocked && achievement.condition_value && (
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ 
                        width: `${Math.min(100, (progress / achievement.condition_value) * 100)}%`,
                        backgroundColor: rarityColors.border,
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredAchievements.length === 0 && (
        <p className="empty-text">没有符合条件的成就</p>
      )}

      <style>{`
        .achievement-list {
          padding: 10px 0;
        }

        .achievement-stats {
          display: flex;
          gap: 30px;
          margin-bottom: 20px;
          padding: 15px;
          background: #f9f9f9;
          border-radius: 8px;
        }

        .achievement-stats .stat {
          text-align: center;
        }

        .achievement-stats .stat-value {
          font-size: 24px;
          font-weight: bold;
          color: #667eea;
          display: block;
        }

        .achievement-stats .stat-label {
          font-size: 14px;
          color: #666;
        }

        .filters {
          display: flex;
          gap: 20px;
          margin-bottom: 20px;
        }

        .filter-group {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .filter-group label {
          font-size: 14px;
          color: #666;
        }

        .filter-group select {
          padding: 6px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }

        .achievements-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 15px;
        }

        .achievement-card {
          display: flex;
          gap: 15px;
          padding: 15px;
          border: 2px solid;
          border-radius: 10px;
          transition: all 0.2s;
        }

        .achievement-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .achievement-card.locked {
          opacity: 0.7;
        }

        .achievement-icon {
          font-size: 40px;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: white;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .achievement-info {
          flex: 1;
          min-width: 0;
        }

        .achievement-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 5px;
        }

        .achievement-name {
          font-weight: 600;
          font-size: 16px;
        }

        .achievement-rarity {
          font-size: 11px;
          padding: 2px 8px;
          border-radius: 10px;
        }

        .achievement-desc {
          font-size: 13px;
          color: #666;
          margin: 0 0 8px 0;
        }

        .achievement-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
        }

        .achievement-points {
          color: #ff9800;
          font-weight: 500;
        }

        .achievement-progress {
          color: #666;
        }

        .achievement-date {
          color: #4caf50;
        }

        .progress-bar {
          height: 4px;
          background: rgba(0,0,0,0.1);
          border-radius: 2px;
          margin-top: 8px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          border-radius: 2px;
          transition: width 0.3s;
        }

        .empty-text {
          text-align: center;
          color: #999;
          padding: 40px;
        }
      `}</style>
    </div>
  );
};

export default AchievementList;
